import { FileTextIcon } from "@phosphor-icons/react/dist/csr/FileText";
import { FlagIcon } from "@phosphor-icons/react/dist/csr/Flag";
import { useMemo, useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@volca/ui/components/ui/empty";
import { LoadingCount } from "@volca/ui/components/ui/loading-count";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import { IcmStatementSummaryCard } from "@/icm/icm-statement-page/statement-summary-card";
import { groupLineItemsByRule } from "@/icm/icm-statement-page/utils";
import type { IcmLineItem } from "@/icm/types";
import { PeriodSelector } from "./period-selector";
import { StatementLineItemCard } from "./statement-line-item-card";
import { TechnicianFeedbackSheet } from "./technician-feedback-sheet";
import { TechnicianLineItemSheet } from "./technician-line-item-sheet";
import type { TechnicianStatementExperienceProps } from "./types";

export const TechnicianStatementExperience = ({
  statement,
  isLoading,
  periodOptions,
  selectedPeriodStart,
  onPeriodChange,
  emptyTitle,
  emptyDescription,
}: TechnicianStatementExperienceProps) => {
  const [selectedLineItem, setSelectedLineItem] = useState<IcmLineItem | null>(
    null,
  );
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const lineItemsByRule = useMemo(
    () =>
      statement
        ? groupLineItemsByRule(
            (statement.line_items ?? []).filter((item) => item.job_id != null),
          )
        : {},
    [statement],
  );

  if (isLoading) {
    return (
      <div className="flex h-page items-center justify-center">
        <Spinner layout="centered" />
      </div>
    );
  }

  return (
    <>
      <div className="h-page w-full overflow-y-auto">
        <Stack
          align="start"
          gap={6}
          className="mx-auto min-h-full w-full max-w-2xl px-4 pt-4 pb-8 sm:px-6 sm:pt-6"
        >
          <PeriodSelector
            options={periodOptions}
            value={selectedPeriodStart}
            onValueChange={onPeriodChange}
          />

          {statement ? (
            <>
              <IcmStatementSummaryCard statement={statement} hideTotalPay />

              <Stack className="w-full" align="start" gap={8}>
                {Object.entries(lineItemsByRule).map(([ruleName, items]) => (
                  <Stack
                    className="w-full"
                    align="start"
                    key={ruleName}
                    gap={2}
                  >
                    <Stack direction="row" align="center" gap={1}>
                      <Text variant="title">{ruleName}</Text>
                      <LoadingCount count={items.length} variant="subtle" />
                    </Stack>

                    <div className="flex w-full flex-col gap-2">
                      {items.map((item) => (
                        <StatementLineItemCard
                          key={item.id}
                          lineItem={item}
                          onClick={() => setSelectedLineItem(item)}
                        />
                      ))}
                    </div>
                  </Stack>
                ))}
              </Stack>

              <div className="flex w-full justify-center">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setFeedbackOpen(true)}
                >
                  <FlagIcon className="h-5 w-5" data-icon="inline-start" />
                  Dispute This Statement
                </Button>
              </div>
            </>
          ) : (
            <Empty className={emptyStateVariants({ layout: "panel" })}>
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <FileTextIcon aria-hidden />
                </EmptyMedia>
                <EmptyTitle>{emptyTitle}</EmptyTitle>
                <EmptyDescription>{emptyDescription}</EmptyDescription>
              </EmptyHeader>
            </Empty>
          )}
        </Stack>
      </div>

      <TechnicianLineItemSheet
        lineItem={selectedLineItem}
        open={!!selectedLineItem}
        onOpenChange={(open) => !open && setSelectedLineItem(null)}
      />

      <TechnicianFeedbackSheet
        open={feedbackOpen}
        onOpenChange={setFeedbackOpen}
        statement={statement}
      />
    </>
  );
};
